import { useMemo } from 'react';

import { useServiceTypeTemplateCtx } from '@/app/(app)/settings/service_types/[id]/provider';
import { Select } from '@/components/atoms/select';
import { ApiSchemas } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';

const COVERAGE_OPTIONS = [
  { value: 'NONE', label: 'None' },
  { value: 'COVERED', label: 'Covered' },
  { value: 'EXCLUDED', label: 'Excluded' },
];

type Coverage = (typeof COVERAGE_OPTIONS)[number]['value'];

export function ServiceTypeTemplatePests() {
  const {
    office: { issues },
  } = useOfficeCtx();

  return (
    <div className='flex w-full flex-col gap-8'>
      <div className='flex flex-col gap-1'>
        <h2 className='text-heading-md'>Pests Covered</h2>
        <p className='text-sm text-muted-foreground'>Select the pests that are covered by this service type.</p>
      </div>
      <div className='flex flex-col'>
        {issues.map((issue) => (
          <IssueCoverageItem key={issue.id} issue={issue} />
        ))}
      </div>
    </div>
  );
}

interface IssueCoverageItemProps {
  issue: ApiSchemas['IssueRead'];
}

function IssueCoverageItem({ issue }: IssueCoverageItemProps) {
  const {
    serviceTypeTemplate: { issues: currentIssues },
    update,
  } = useServiceTypeTemplateCtx();
  const coverage = useMemo(() => {
    const value = currentIssues.find((i) => i.issue_id === issue.id);
    if (!value) return 'NONE';
    return value.excluded ? 'EXCLUDED' : 'COVERED';
  }, [issue, currentIssues]);

  const setIssueCoverage = (issueId: string, newValue: Coverage) => {
    if (newValue === 'NONE') {
      update({
        issues: currentIssues.filter((issue) => issue.issue_id !== issueId),
      });
      return;
    }

    if (currentIssues.find((i) => i.issue_id === issueId)) {
      update({
        issues: currentIssues.map((i) => (i.issue_id === issueId ? { ...i, excluded: newValue === 'EXCLUDED' } : i)),
      });
    } else {
      update({
        issues: [
          ...currentIssues,
          {
            id: issueId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            external_id: null,
            issue_id: issueId,
            notes: null,
            excluded: newValue === 'EXCLUDED',
          },
        ],
      });
    }
  };

  return (
    <div key={issue.id} className='flex flex-row items-center justify-between border-b-[0.5px] px-3 py-2'>
      <div className='text-sm font-medium'>{issue.name}</div>
      <Select
        options={COVERAGE_OPTIONS}
        value={coverage}
        onChange={(value) => setIssueCoverage(issue.id, value)}
        className='w-40'
      />
    </div>
  );
}
